import apiClient from "@/services/api/client";
import urlBuilder from "@/services/api/urlBuilder";
import { DEFAULT_ORDERS_PAGE_SIZE } from "@/shared/utils/constants";
import { buildOrdersGlobalFilter, type OrdersGlobalFilterInput } from "../buildOrdersGlobalFilter";

// V4 orders read API (Task 6). OrderV4 is a separate collection with its own
// endpoints (order-getOrdersV4 / order-getOrderV4) and id space — kept in a
// dedicated module so the large v3 ordersApi.ts is untouched.

export interface ParcelDimensions {
  length_cm?: number;
  width_cm?: number;
  height_cm?: number;
  weight_kg?: number;
}

export interface OrderV4Parcel {
  parcel_index?: number;
  description?: string;
  declared?: ParcelDimensions | null;
  /** Hub-measured dims/weight when available, else mirrors declared. */
  effective?: ParcelDimensions | null;
  volumetric_weight_kg?: number | null;
  billable_weight_kg?: number | null;
  items_packed?: Array<{ name?: string; sku?: string; quantity?: number }>;
}

export interface OrderV4Shipment {
  _id?: string;
  waybill?: string;
  status?: string;
  milestone?: string;
  parcels?: OrderV4Parcel[];
  delivery?: {
    contact?: { name?: string; phone?: string };
    address?: { formatted?: string };
    proof?: { pdf_link?: string | null } | null;
  } | null;
  // courier arrives as the populated object (shipment.courier), not a string.
  // `service_type` is the legacy string enum (never written); the service type
  // resolved at creation/booking is snapshotted onto the *_code / *_name fields,
  // so nothing here needs a populate.
  courier?: {
    service_type?: string;
    service_type_code?: string;
    service_type_name?: string;
    /** "HH:mm" snapshot of the service type's booking cut-off. */
    booking_cutoff_time?: string;
    provider?: string;
    cost?: number;
    planned?: {
      collection_at?: string | null;
      at_hub_at?: string | null;
      delivery_at?: string | null;
    } | null;
  } | null;
  shipment_type?: string;
  // breaze_driver / breaze_manifest may arrive populated (object) or as a bare id.
  breaze_driver?: { _id?: string; name?: string } | string | null;
  breaze_manifest?: { _id?: string; manifest_key?: string } | string | null;
  // Tookan-side assignment write-back; `driver` is null when the fleet is unmapped.
  tookan_driver?: {
    driver?: { _id?: string; name?: string } | string | null;
    fleet_id?: number;
    fleet_name?: string;
    assigned_at?: string;
    job_status?: number;
    leg?: string;
  } | null;
  [key: string]: unknown;
}

export interface OrderV4 {
  _id: string;
  breaze_order_key?: string;
  status?: string;
  // tri-state: true | false | null (null = eligibility indeterminate, e.g. a
  // region-API outage at create time). Render null as "Unknown", never "No".
  breaze_eligible?: boolean | null;
  platform?: string;
  createdAt?: string;
  updatedAt?: string;
  order_id?: string;
  merchant_reference?: string;
  line_items?: Array<{
    name?: string;
    quantity?: number;
    selling_price?: number;
    cost_price?: number;
  }>;
  financials?: {
    sub_total?: number;
    shipping_fee?: number;
    total?: number;
    currency?: string;
  };
  note?: string;
  customer?: {
    name?: string;
    email?: string;
    phone?: string;
    breaze_ref?: string | { _id?: string };
  } | null;
  address?: Record<string, unknown> | null;
  breaze_merchant?: { _id?: string; name?: string } | null;
  breaze_store?: { _id?: string; store_name?: string } | null;
  breaze_location?: { _id?: string; name?: string; address?: string } | null;
  shipments?: OrderV4Shipment[];
  platform_metadata?: unknown;
  [key: string]: unknown;
}

export interface OrdersV4Response {
  docs: OrderV4[];
  page: number;
  totalPages: number;
  totalDocs: number;
  limit?: number;
  hasNextPage?: boolean;
  hasPrevPage?: boolean;
}

export interface GetOrdersV4Params {
  page?: number;
  limit?: number;
  globalFilterEnabled?: boolean;
  globalFilter?: {
    status?: string | string[];
    /** Shipment-level status/milestone, separate from the order-level `status`. */
    shipmentStatus?: string | string[];
    platform?: string | string[];
    orderId?: string;
    orderIds?: string[];
    waybill?: string;
    waybills?: string[];
    breaze_manifests?: string[];
    customer?: string;
    dateFrom?: string;
    dateTo?: string;
    status_not_in?: string;
    past_sla?: boolean;
    stale?: boolean;
    updatedAtFrom?: string;
    updatedAtTo?: string;
  };
  merchantId?: string;
  storeId?: string;
  locationId?: string;
  driverId?: string;
  /** Note: the backend gives `locationId` priority over `hubId` when both are set. */
  hubId?: string;
  csv?: boolean;
  csvDetailed?: boolean;
}

export type OrdersV4ListFilters = OrdersGlobalFilterInput & {
  merchantId?: string;
  storeId?: string;
  locationId?: string;
  driverId?: string;
  hubId?: string;
  drillGlobalExtras?: Record<string, string | boolean>;
};

export function buildOrdersV4ListParams(options: {
  page: number;
  limit: number;
  filters: OrdersV4ListFilters;
  csv?: boolean;
  csvDetailed?: boolean;
}): GetOrdersV4Params {
  const { filters } = options;
  const globalFilter: NonNullable<GetOrdersV4Params["globalFilter"]> = {
    ...buildOrdersGlobalFilter(filters),
  };
  if (filters.drillGlobalExtras) {
    for (const [key, value] of Object.entries(filters.drillGlobalExtras)) {
      if (value !== undefined && value !== "") {
        (globalFilter as Record<string, unknown>)[key] = value;
      }
    }
  }
  return {
    page: options.page,
    limit: options.limit,
    globalFilter,
    merchantId: filters.merchantId || undefined,
    storeId: filters.storeId || undefined,
    locationId: filters.locationId || undefined,
    driverId: filters.driverId || undefined,
    hubId: filters.hubId || undefined,
    csv: options.csv,
    csvDetailed: options.csvDetailed,
  };
}

export function buildOrdersV4CsvExportParams(
  listParams: GetOrdersV4Params,
  options?: { csvDetailed?: boolean }
): GetOrdersV4Params {
  return {
    ...listParams,
    page: 1,
    csv: true,
    csvDetailed: options?.csvDetailed ?? listParams.csvDetailed,
  };
}

function buildOrdersV4RequestBody(params: GetOrdersV4Params): Record<string, unknown> {
  const requestBody: Record<string, unknown> = {
    globalFilterEnabled: params.globalFilterEnabled !== false,
    globalFilter: params.globalFilter ?? {},
  };
  if (params.storeId) requestBody.storeId = params.storeId;
  if (params.locationId) requestBody.locationId = params.locationId;
  if (params.driverId) requestBody.driverId = params.driverId;
  if (params.merchantId) requestBody.merchantId = params.merchantId;
  if (params.hubId) requestBody.hubId = params.hubId;
  if (params.csv) requestBody.csv = true;
  if (params.csvDetailed) requestBody.csvDetailed = true;
  return requestBody;
}

/** Paginated V4 orders list. Page/limit are query params; filters go in the POST body (mirrors getOrdersV2). */
export async function getOrdersV4(params: GetOrdersV4Params = {}): Promise<OrdersV4Response> {
  const page = params.page ?? 1;
  const limit = params.limit ?? DEFAULT_ORDERS_PAGE_SIZE;
  const url = `${urlBuilder.buildUrl("order-getOrdersV4")}?limit=${limit}&page=${page}`;
  // This endpoint can run long on large/filtered pages — give it more room than
  // the client's default 30s timeout.
  const response = await apiClient.post(url, buildOrdersV4RequestBody(params), {
    timeout: 120000,
  });
  const data = (response.data?.data ?? response.data ?? {}) as Partial<OrdersV4Response>;
  return {
    docs: data.docs ?? [],
    page: data.page ?? page,
    totalPages: data.totalPages ?? 1,
    totalDocs: data.totalDocs ?? data.docs?.length ?? 0,
    limit: data.limit ?? limit,
    hasNextPage: data.hasNextPage,
    hasPrevPage: data.hasPrevPage,
  };
}

/** CSV export for V4 orders — same filter envelope as the list. */
export async function exportOrdersV4Csv(params: GetOrdersV4Params): Promise<Blob> {
  const page = 1;
  const limit = params.limit || DEFAULT_ORDERS_PAGE_SIZE;
  const url = `${urlBuilder.buildUrl("order-getOrdersV4")}?limit=${limit}&page=${page}`;
  const response = await apiClient.post(
    url,
    buildOrdersV4RequestBody({ ...params, csv: true, csvDetailed: !!params.csvDetailed }),
    { responseType: "blob", timeout: 120000 }
  );
  return response.data as Blob;
}

/** Single V4 order by id. getOrderV4 returns `{ order }`. */
export async function getOrderV4(orderId: string): Promise<OrderV4 | null> {
  const response = await apiClient.post(urlBuilder.buildUrl("order-getOrderV4"), { orderId });
  const raw = response.data?.data ?? response.data;
  if (raw && typeof raw === "object" && "order" in raw && raw.order) {
    return raw.order as OrderV4;
  }
  return (raw as OrderV4) ?? null;
}

export interface ShipmentActionV4Payload {
  /** The source shipment's waybill — both Ship Again and Return only need this. */
  waybill: string;
}

export interface ShipmentActionV4Result {
  waybill: string;
  shipment_id: string;
  source_waybill: string;
  breaze_eligible: boolean | null;
  milestone: string;
}

/** Books a fresh delivery on the same order, reusing the source shipment's details. */
export async function shipAgainV4(
  payload: ShipmentActionV4Payload
): Promise<ShipmentActionV4Result> {
  const response = await apiClient.post(urlBuilder.buildUrl("order-shipAgainV4"), payload);
  const data = response.data?.data ?? response.data;
  return data as ShipmentActionV4Result;
}

/** Creates a reverse-direction (customer → merchant) shipment on the same order. */
export async function returnShipmentV4(
  payload: ShipmentActionV4Payload
): Promise<ShipmentActionV4Result> {
  const response = await apiClient.post(urlBuilder.buildUrl("order-returnShipmentV4"), payload);
  const data = response.data?.data ?? response.data;
  return data as ShipmentActionV4Result;
}

export interface RtoShipmentV4Result extends ShipmentActionV4Result {
  status: string;
}

/**
 * Admin-only. Creates an internal hub → merchant Return To Origin shipment from a
 * source shipment currently sitting `At Hub` or `Failed` — never involves the
 * customer. `breaze_eligible` can be true/false/null even on a `success: true`
 * response; only `true` (or `milestone === 'InManifest'`) means it's fully booked.
 */
export async function rtoShipmentV4(
  payload: ShipmentActionV4Payload
): Promise<RtoShipmentV4Result> {
  const response = await apiClient.post(urlBuilder.buildUrl("order-rtoShipmentV4"), payload);
  const data = response.data?.data ?? response.data;
  return data as RtoShipmentV4Result;
}

export interface IncreaseShipmentParcelsV4Payload {
  waybill: string;
  /** Target total parcel count, not an increment — must be > current count and <= 4. */
  parcel_count: number;
}

export interface IncreaseShipmentParcelsV4Result {
  waybill: string;
  parcel_count: number;
  /** Same printer-queue/{jobId} job the existing auto-print flow already polls. */
  jobId: string;
}

/**
 * Any authenticated portal user, same as updateOrderV4 (not admin-only). Reprints EVERY
 * parcel on the shipment, not just the new ones — each label encodes its position ("N of
 * total"), so a count change makes all previously-printed labels for this shipment stale.
 * New parcels clone the shipment's last parcel's dimensions (or the merchant's default
 * package type) — there's no way to specify custom dimensions through this endpoint.
 */
export async function increaseShipmentParcelsV4(
  payload: IncreaseShipmentParcelsV4Payload
): Promise<IncreaseShipmentParcelsV4Result> {
  const response = await apiClient.post(
    urlBuilder.buildUrl("order-increaseShipmentParcelsV4"),
    payload
  );
  const data = response.data?.data ?? response.data;
  return data as IncreaseShipmentParcelsV4Result;
}

export type ShipmentLabelEventAction = "print" | "download";

/**
 * Fire-and-forget usage logging for a shipment label print/download — never awaited by
 * callers, and failures are swallowed here so a logging hiccup can never surface to the
 * user or interfere with the actual print/download it's recording. Not applicable to
 * packing slips — only shipping-label prints/downloads get logged.
 */
export function logShipmentLabelEvent(waybill: string, action: ShipmentLabelEventAction): void {
  if (!waybill) return;
  void apiClient
    .post(urlBuilder.buildUrl("order-logShipmentLabelEventV4"), { waybill, action })
    .catch(() => {});
}

/** Dedupes waybills (a multi-parcel shipment can appear more than once in one batch) before
 *  firing one independent, non-awaited log call per waybill. */
export function logShipmentLabelEvents(
  waybills: Array<string | undefined>,
  action: ShipmentLabelEventAction
): void {
  const unique = new Set(waybills.filter((w): w is string => !!w));
  for (const waybill of unique) {
    logShipmentLabelEvent(waybill, action);
  }
}

export interface ShipmentLabelEventCounts {
  waybill: string;
  print_count: number;
  download_count: number;
}

/**
 * Checked before a single-shipment PDF/Zebra bulk action, to offer a "you've already done
 * this N times — redownload/reprint, or increase parcels first?" confirmation. This is a
 * nice-to-have guard, never a gate on the real action: any failure here (network error,
 * 404, etc.) resolves to `null` rather than throwing, so callers can fail open and proceed
 * with the print/download as if there were no prior events.
 */
export async function getShipmentLabelEventCountsV4(
  waybill: string
): Promise<ShipmentLabelEventCounts | null> {
  try {
    const response = await apiClient.post(
      urlBuilder.buildUrl("order-getShipmentLabelEventCountsV4"),
      { waybill }
    );
    const data = response.data?.data ?? response.data;
    return data as ShipmentLabelEventCounts;
  } catch {
    return null;
  }
}

// --- V4 custom-order creation (Task 7) — portal sibling of v3 createCustomOrder. ---

export interface CreateCustomOrderV4Address {
  formatted?: string;
  address1?: string;
  city?: string;
  province?: string;
  zip?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
}

export interface CreateCustomOrderV4Parcel {
  length_cm: number;
  width_cm: number;
  height_cm: number;
  weight_kg: number;
}

export interface CreateCustomOrderV4Payload {
  storeId: string;
  pickupLocationId: string;
  orderId?: string;
  customer: { ref?: string; name: string; email?: string; phone?: string };
  delivery: { address: CreateCustomOrderV4Address; note?: string };
  /** 1 to 4 parcels — each becomes its own delivery shipment (own waybill) server-side. */
  parcels: CreateCustomOrderV4Parcel[];
  /** Set to 1 to immediately book every Breaze-Eligible shipment with a driver. */
  request_shipment?: 0 | 1;
}

/** The created OrderV4 (with `_id` for navigation) plus the backend status message. */
export interface CreateCustomOrderV4Result {
  order: OrderV4;
  /** Backend status message — surfaces partial `request_shipment` booking failures. */
  message: string;
}

export async function createCustomOrderV4(
  payload: CreateCustomOrderV4Payload
): Promise<CreateCustomOrderV4Result> {
  const response = await apiClient.post(urlBuilder.buildUrl("order-createCustomOrderV4"), payload);
  const body = response.data ?? {};
  const raw = body?.data ?? body;
  const order = (
    raw && typeof raw === "object" && "order" in raw && raw.order ? raw.order : raw
  ) as OrderV4;
  const message = typeof body?.message === "string" ? body.message : "";
  return { order, message };
}

// --- V4 order edit — portal sibling of updateOrder (JWT), backed by order-updateOrderV4. ---

export interface UpdateOrderV4Parcel {
  description?: string;
  declared: ParcelDimensions;
  items_packed?: Array<{ name?: string; sku?: string; quantity?: number }>;
}

/** Parcels are keyed to a shipment by waybill (preferred) or _id. */
export interface UpdateOrderV4ShipmentInput {
  waybill?: string;
  _id?: string;
  parcels: UpdateOrderV4Parcel[];
}

export interface UpdateOrderV4Payload {
  orderId: string;
  order_id?: string;
  customer?: { name?: string; email?: string; phone?: string };
  delivery?: { address?: CreateCustomOrderV4Address; note?: string };
  shipments?: UpdateOrderV4ShipmentInput[];
}

export async function updateCustomOrderV4(payload: UpdateOrderV4Payload): Promise<OrderV4> {
  const response = await apiClient.post(urlBuilder.buildUrl("order-updateOrderV4"), payload);
  const body = response.data ?? {};
  const raw = body?.data ?? body;
  return (
    raw && typeof raw === "object" && "order" in raw && raw.order ? raw.order : raw
  ) as OrderV4;
}

// --- Collection Failed reinstate — admin-only manifest-requestCollectionReattempt. ---

export interface RequestCollectionReattemptPayload {
  shipmentId: string;
}

export interface RequestCollectionReattemptResult {
  shipmentId?: string;
  waybill?: string;
  milestone?: string;
  status?: string;
}

export async function requestCollectionReattempt(
  payload: RequestCollectionReattemptPayload
): Promise<RequestCollectionReattemptResult> {
  const response = await apiClient.post(
    urlBuilder.buildUrl("manifest-requestCollectionReattempt"),
    { shipmentId: payload.shipmentId }
  );
  return (response.data?.data ?? response.data ?? {}) as RequestCollectionReattemptResult;
}

// --- Comments — internal notes on an order or a shipment, rolled up by order.
// Admins compose and edit; every role can read (the backend scopes and redacts). ---

export type CommentVisibility = "private" | "public";

export interface ShipmentComment {
  id: string;
  scope: "order" | "shipment";
  shipment_id: string | null;
  waybill: string | null;
  order_id: string | null;
  comment: string;
  visibility: string;
  author: string | null;
  author_id: string | null;
  created_at: string;
  edited_at: string | null;
  edited_by: string | null;
  editor: string | null;
}

export interface AddShipmentCommentPayload {
  shipmentId?: string;
  waybill?: string;
  orderId?: string;
  comment: string;
  visibility?: CommentVisibility;
}

/** Only the supplied fields are written; the backend rejects a payload with neither. */
export interface EditCommentPayload {
  commentId: string;
  comment?: string;
  visibility?: CommentVisibility;
}

/** Identifier precedence: shipmentId > waybill > orderId. Extras are ignored, never a 400. */
export interface ListShipmentCommentsParams {
  orderId?: string;
  shipmentId?: string;
  waybill?: string;
}

export interface ListShipmentCommentsResult {
  comments: ShipmentComment[];
  truncated: boolean;
}

export async function addShipmentComment(
  payload: AddShipmentCommentPayload
): Promise<ShipmentComment> {
  const response = await apiClient.post(urlBuilder.buildUrl("order-addShipmentCommentV4"), payload);
  return (response.data?.data ?? response.data ?? {}) as ShipmentComment;
}

export async function editComment(payload: EditCommentPayload): Promise<ShipmentComment> {
  const response = await apiClient.post(urlBuilder.buildUrl("order-editCommentV4"), payload);
  return (response.data?.data ?? response.data ?? {}) as ShipmentComment;
}

export async function listShipmentComments(
  params: ListShipmentCommentsParams
): Promise<ListShipmentCommentsResult> {
  const response = await apiClient.post(
    urlBuilder.buildUrl("order-listShipmentCommentsV4"),
    params
  );
  const data = (response.data?.data ?? response.data ?? {}) as Partial<ListShipmentCommentsResult>;
  return { comments: data.comments ?? [], truncated: data.truncated ?? false };
}
